import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import type {
  GetUnreadParams,
  UnreadNotification
} from 'api/messageCommunication/types';

import { KEYS } from 'helpers/react-query.config';
import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import type { PagedList } from 'interfaces/general';

type Props = Partial<GetUnreadParams> & {
  enabled: boolean;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setNotifications?: React.Dispatch<React.SetStateAction<UnreadNotification[]>>;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
};

const DEFAULT_PARAMS = {
  pageNumber: 1,
  pageSize: 200,
  descending: false,
  text: ''
};

function useGetUnreadFromHttp({
  enabled,
  receiverUserId,
  setEnabled,
  setNotifications,
  setTotalCount
}: Props) {
  const [params, setParams] = useState<GetUnreadParams>({
    ...DEFAULT_PARAMS,
    receiverUserId
  });
  const { showApiError } = useGlobalContext();
  const { messageCommunication: messageCommunicationApi } = useApiContext();
  const queryKey = [
    KEYS.messageCommunication.notifications.getUnreadFromHttp,
    params
  ];

  const onError = useCallback(
    (error: unknown) => {
      showApiError(error, 'Error al obtener las notificaciones no leídas');
    },
    [showApiError]
  );

  const onSuccess = useCallback(
    (response: PagedList<UnreadNotification>) => {
      setEnabled(false);

      if (params.pageNumber === 1) {
        setNotifications(response.items);
        setTotalCount(response.totalCount);
      } else {
        setNotifications((prevNotifications) => [
          ...prevNotifications,
          ...response.items
        ]);
      }
    },
    [params.pageNumber, setEnabled, setNotifications, setTotalCount]
  );

  const {
    data: unreadNotifications,
    isLoading: isUnreadNotificationsLoading,
    refetch: refetchUnreadNotifications,
    isFetching: isUnreadNotificationsFetching
  } = useQuery(
    queryKey,
    () => messageCommunicationApi.notifications.getUnread(params),
    {
      cacheTime: 0,
      enabled,
      onError,
      onSuccess,
      refetchOnWindowFocus: false,
      select: (response) => response.data.data
    }
  );

  const setUnreadNotificationsParams = useCallback(
    (newParams?: Partial<GetUnreadParams>) => {
      setParams((prevParams) => ({
        ...prevParams,
        ...newParams
      }));
    },
    []
  );

  return {
    isUnreadNotificationsFetching,
    isUnreadNotificationsLoading,
    params,
    refetchUnreadNotifications,
    setUnreadNotificationsParams,
    unreadNotifications
  };
}

export default useGetUnreadFromHttp;
